import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

export interface StatDefinition {
  id?: number;
  category: string;
  table_name: string;
  key_name: string;
  aggregation_type: 'tally' | 'average';
  value_min: number;
  value_max: number;
}

export interface StatsDefinitionDialogData {
  isEdit: boolean;
  definition?: StatDefinition;
  prefillCategory?: string;
  prefillTable?: string;
}

@Component({
  selector: 'app-stats-definition-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule
  ],
  templateUrl: './stats-definition-dialog.component.html',
  styleUrl: './stats-definition-dialog.component.less'
})
export class StatsDefinitionDialogComponent implements OnInit {
  form: FormGroup;
  isEdit: boolean;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<StatsDefinitionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: StatsDefinitionDialogData
  ) {
    this.isEdit = data.isEdit;
    this.form = this.fb.group({
      category: ['', [Validators.required, Validators.maxLength(255)]],
      table_name: ['', [Validators.required, Validators.maxLength(255)]],
      key_name: ['', [Validators.required, Validators.maxLength(255)]],
      aggregation_type: ['tally', [Validators.required]],
      value_min: [0, [Validators.required, Validators.pattern(/^-?\d+(\.\d{1,4})?$/)]],
      value_max: [100, [Validators.required, Validators.pattern(/^-?\d+(\.\d{1,4})?$/)]]
    });
  }

  ngOnInit() {
    if (this.isEdit && this.data.definition) {
      this.form.patchValue(this.data.definition);
    } else {
      // Pre-fill category and/or table if provided
      if (this.data.prefillCategory) {
        this.form.patchValue({ category: this.data.prefillCategory });
      }
      if (this.data.prefillTable) {
        this.form.patchValue({ table_name: this.data.prefillTable });
      }
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSave() {
    if (this.form.valid) {
      const formValue = this.form.value;
      
      // Validate min <= max (allow equal values)
      if (formValue.value_min > formValue.value_max) {
        this.form.get('value_max')?.setErrors({ minMaxError: true });
        return;
      }

      const result: StatDefinition = {
        ...formValue,
        value_min: parseFloat(formValue.value_min),
        value_max: parseFloat(formValue.value_max)
      };

      if (this.isEdit && this.data.definition?.id) {
        result.id = this.data.definition.id;
      }

      this.dialogRef.close(result);
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
    }
  }

  getErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (field?.hasError('required')) {
      return `${fieldName.replace('_', ' ')} is required`;
    }
    if (field?.hasError('maxlength')) {
      return `${fieldName.replace('_', ' ')} is too long`;
    }
    if (field?.hasError('pattern')) {
      return `${fieldName.replace('_', ' ')} must be a valid number`;
    }
    if (field?.hasError('minMaxError')) {
      return 'Max value must be greater than or equal to min value';
    }
    return '';
  }
}
